import { Extension } from '@tiptap/react'
import { PluginKey } from '@tiptap/pm/state'
import { Suggestion } from '@tiptap/suggestion'

// The "/" menu into a text chart's data fields, in two steps: pick the field,
// then pick what to print about it. Plain DOM rather than a ReactRenderer: the
// editor sits inside a Bootstrap modal, and a fixed node on document.body is
// what clears its stacking context.

export const SUGGESTION_CHAR = '/'

const MENU_MARGIN = 4

// Flattens grouped options into the single list the menu renders, narrowed by
// whatever was typed after the "/".
export const filterFieldItems = (groups = [], query = '') => {
  const needle = query.trim().toLowerCase()

  return (groups || []).flatMap((group) =>
    (group.options || [])
      .filter((option) => !needle || option.label.toLowerCase().includes(needle))
      .map((option) => ({ ...option, group: group.label }))
  )
}

export const createFilterFieldMenu = ({ getCalculations = () => [] } = {}) => {
  const element = document.createElement('div')
  element.className = 'filter-field-menu'
  element.setAttribute('role', 'listbox')

  let items = []
  let selected = 0
  let field = null
  let latest = null

  const isFieldStage = () => field === null

  const renderHeading = (text, className) => {
    const heading = document.createElement('div')
    heading.className = className
    heading.textContent = text
    element.appendChild(heading)
  }

  const renderItems = () => {
    element.replaceChildren()
    // allowSpaces keeps the query alive across a sentence, so a stray "/" would
    // park an empty menu over the editor until the paragraph ended.
    element.hidden = items.length === 0
    if (items.length === 0) return

    if (field) renderHeading(`${field.label} ›`, 'filter-field-menu-field')

    let lastGroup
    items.forEach((item, index) => {
      if (item.group !== lastGroup) {
        lastGroup = item.group
        renderHeading(item.group, 'filter-field-menu-heading')
      }

      const option = document.createElement('button')
      option.type = 'button'
      option.className = index === selected
        ? 'filter-field-menu-item is-selected'
        : 'filter-field-menu-item'
      option.setAttribute('role', 'option')
      option.setAttribute('aria-selected', String(index === selected))
      option.textContent = item.label

      // The value a filter would print, so the author sees what they are about
      // to insert rather than discovering it on the rendered chart.
      if (item.current) {
        const current = document.createElement('span')
        current.className = 'filter-field-menu-current'
        current.textContent = item.current
        option.appendChild(current)
      }

      // mousedown, not click: by the time click fires the editor has lost the
      // selection to the button.
      option.addEventListener('mousedown', (event) => {
        event.preventDefault()
        choose(items[index])
      })
      element.appendChild(option)
    })
  }

  const position = () => {
    const rect = latest?.clientRect?.()
    if (!rect) return

    element.style.left = `${rect.left}px`
    const below = rect.bottom + MENU_MARGIN
    const overflows = below + element.offsetHeight > window.innerHeight
    element.style.top = overflows
      ? `${Math.max(MENU_MARGIN, rect.top - element.offsetHeight - MENU_MARGIN)}px`
      : `${below}px`
  }

  const showCalculations = (chosen) => {
    field = chosen
    items = filterFieldItems(getCalculations(chosen.column))
    selected = 0
    renderItems()
    position()
  }

  const showFields = () => {
    field = null
    items = latest?.items || []
    selected = 0
    renderItems()
    position()
  }

  function choose(item) {
    if (!item) return
    // A field that is already a token (Count) needs no second step.
    if (isFieldStage() && !item.token) return showCalculations(item)

    return latest?.command(item)
  }

  const move = (offset) => {
    selected = (selected + offset + items.length) % items.length
    renderItems()
    // Found by class, not by index into element.children: renderItems interleaves
    // a group heading before each group (and a field heading in step two), so the
    // nth child is not the nth item and the menu would scroll to the wrong row.
    element
      .querySelector('.filter-field-menu-item.is-selected')
      ?.scrollIntoView?.({ block: 'nearest' })
  }

  return {
    element,

    mount: () => document.body.appendChild(element),

    destroy: () => element.remove(),

    update: (props) => {
      latest = props
      // Step two is a list of its own: a query that keeps growing after the
      // field was chosen must not drag the menu back to the field list.
      if (!isFieldStage()) return position()

      items = props.items || []
      selected = 0
      renderItems()
      return position()
    },

    // A false reaches the plugin, which is how Escape gets to its own dismiss.
    onKeyDown: (event) => {
      // Escape backs out of step two rather than closing the whole menu.
      if (event.key === 'Escape' && !isFieldStage()) {
        showFields()
        return true
      }
      if (items.length === 0) return false

      if (event.key === 'ArrowDown') {
        move(1)
        return true
      }
      if (event.key === 'ArrowUp') {
        move(-1)
        return true
      }
      if (event.key === 'Enter' || event.key === 'Tab') {
        choose(items[selected])
        return true
      }
      return false
    }
  }
}

// Split from the extension so the trigger rules and the insert command can be
// exercised without a ProseMirror view.
export const filterFieldSuggestion = ({ getFields, getCalculations }) => ({
  char: SUGGESTION_CHAR,
  // Left explicit: it is what keeps a date ("7/1") or a URL from triggering.
  allowedPrefixes: [' '],
  // Field names are several words, so a query that stopped at the first space
  // could never narrow past "invoice".
  allowSpaces: true,
  startOfLine: false,
  // No fields means no menu, matching the disabled dropdown.
  allow: () => getFields().length > 0,
  // A "/" followed by a space is prose ("revenue / budget"), not a lookup: with
  // allowSpaces the query would otherwise stay live and a matching word would
  // take the Enter meant for a new paragraph.
  items: ({ query }) => (/^\s/.test(query) ? [] : filterFieldItems(getFields(), query)),
  command: ({ editor, range, props }) => {
    editor.chain().focus().deleteRange(range).insertContent(props.token).run()
  },
  render: () => {
    let menu = null

    return {
      onStart: (props) => {
        menu = createFilterFieldMenu({ getCalculations })
        // Mounted first so the flip calculation can measure the menu.
        menu.mount()
        menu.update(props)
      },
      onUpdate: (props) => menu?.update(props),
      onKeyDown: ({ event }) => menu?.onKeyDown(event) || false,
      onExit: () => {
        menu?.destroy()
        menu = null
      }
    }
  }
})

export const FilterFieldSuggestion = Extension.create({
  name: 'filterFieldSuggestion',

  addOptions() {
    return {
      // Getters, not values: the columns arrive after the editor is built.
      getFields: () => [],
      getCalculations: () => []
    }
  },

  addProseMirrorPlugins() {
    return [
      Suggestion({
        editor: this.editor,
        pluginKey: new PluginKey('filterFieldSuggestion'),
        ...filterFieldSuggestion({
          getFields: () => this.options.getFields(),
          getCalculations: (column) => this.options.getCalculations(column)
        })
      })
    ]
  }
})
